'use client';
import React, { useState, useRef, useEffect } from 'react';
import { Link } from '@mokoboko/components';
import { mergeClassNames } from '@/lib/utils';

// The space before the link is the gap, since it drops at a line break. Link's
// negative margin pairs with the padding stripped here, so both go, or a link
// that starts a line sits off the text's left edge.
const TOGGLE_LINK_CLASS = 'm-0 inline p-0 text-sm';

// Text, never markup: toPlainText decodes entities, so studio copy can hold a
// literal "<img onerror>". The label is semibold like the Link it stands for.
const fillMeasure = (
  measure: HTMLElement,
  body: string,
  label: string | null
) => {
  measure.textContent = body;
  if (!label) return;
  const link = document.createElement('span');
  link.className = 'font-semibold';
  link.textContent = label;
  measure.append(' ', link);
};

interface ExpandableTextProps {
  text: string;
  className?: string;
  containerClassName?: string;
  maxLines?: number;
  moreLabel?: string;
  lessLabel?: string;
  children?: React.ReactNode;
}

export const ExpandableText = ({
  text,
  className,
  containerClassName,
  maxLines = 2,
  moreLabel = 'more',
  lessLabel = 'less',
  children,
}: ExpandableTextProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [displayedText, setDisplayedText] = useState(text);
  const [showMoreButton, setShowMoreButton] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLParagraphElement>(null);
  // With children the link shows even when the text fits, so the text must
  // fit beside it or it drops to a line of its own.
  const alwaysToggles = !!children;

  useEffect(() => {
    const calculateTruncation = () => {
      if (!containerRef.current || !measureRef.current) return;

      const container = containerRef.current;
      const measure = measureRef.current;

      measure.style.width = `${container.offsetWidth}px`;
      fillMeasure(measure, text, alwaysToggles ? moreLabel : null);

      const lineHeight = parseFloat(
        window.getComputedStyle(measure).lineHeight || '20'
      );
      const maxHeight = lineHeight * maxLines;

      if (measure.offsetHeight <= maxHeight) {
        setDisplayedText(text);
        setShowMoreButton(false);
        return;
      }

      let start = 0;
      let end = text.length;
      let bestFitIndex = 0;

      while (start <= end) {
        const mid = Math.floor((start + end) / 2);
        const sub = text.substring(0, mid);

        fillMeasure(measure, `${sub}...`, moreLabel);

        if (measure.offsetHeight <= maxHeight) {
          bestFitIndex = mid;
          start = mid + 1;
        } else {
          end = mid - 1;
        }
      }

      setDisplayedText(text.substring(0, bestFitIndex) + '...');
      setShowMoreButton(true);
    };

    calculateTruncation();

    const resizeObserver = new ResizeObserver(calculateTruncation);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [text, maxLines, isExpanded, moreLabel, alwaysToggles]);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  if (!text) return null;

  const canToggle = showMoreButton || !!children;

  return (
    <div
      ref={containerRef}
      className={mergeClassNames('relative', containerClassName)}
    >
      <p
        ref={measureRef}
        className={mergeClassNames(
          'invisible absolute -z-10 w-full whitespace-normal break-words text-sm text-gray-700',
          className
        )}
        aria-hidden="true"
      />

      {!isExpanded ? (
        <p className={mergeClassNames('text-sm text-gray-700', className)}>
          {displayedText}
          {canToggle && (
            <>
              {' '}
              <Link onClick={handleToggle} className={TOGGLE_LINK_CLASS}>
                {moreLabel}
              </Link>
            </>
          )}
        </p>
      ) : (
        <>
          <p className={mergeClassNames('text-sm text-gray-700', className)}>
            {text}{' '}
            <Link onClick={handleToggle} className={TOGGLE_LINK_CLASS}>
              {lessLabel}
            </Link>
          </p>
          {children}
        </>
      )}
    </div>
  );
};
